import { formatDate, isValidDate, parseDate } from './dateUtils'
import { mockExpenses, mockEmployees } from './mockData'
import { formatCurrency } from './formatUtils'
/**
 * Safely calculates the total amount for a collection of expenses
 *
 * @param expenses - Array of expense objects
 * @returns Total amount as a number
 */
export const calculateTotalAmount = (expenses: any[]): number => {
  if (!Array.isArray(expenses)) return 0
  return expenses.reduce((total, expense) => {
    const amount = parseFloat(expense.amount)
    return total + (isNaN(amount) ? 0 : amount)
  }, 0)
}
/**
 * Filters expenses by date range
 *
 * @param expenses - Array of expense objects
 * @param startDate - Start date of the range
 * @param endDate - End date of the range
 * @returns Filtered array of expenses
 */
export const filterExpensesByDateRange = (
  expenses: any[],
  startDate: string | Date,
  endDate: string | Date,
): any[] => {
  if (!Array.isArray(expenses)) return []
  // Parse dates
  const start = parseDate(startDate)
  const end = parseDate(endDate)
  // Return all expenses if dates are invalid
  if (!start || !end) {
    console.warn('Invalid date range provided', { startDate, endDate })
    return expenses
  }
  // Set end date to end of day to include the end date in the range
  end.setHours(23, 59, 59, 999)
  return expenses.filter((expense) => {
    const expenseDate = parseDate(expense.date)
    if (!expenseDate) return false
    return expenseDate >= start && expenseDate <= end
  })
}
/**
 * Groups expenses by a specified property (e.g., type, category)
 *
 * @param expenses - Array of expense objects
 * @param groupBy - Property name to group by
 * @returns Object with groups as keys and arrays of expenses as values
 */
export const groupExpensesBy = (
  expenses: any[],
  groupBy: string,
): Record<string, any[]> => {
  if (!Array.isArray(expenses)) return {}
  return expenses.reduce((groups, expense) => {
    const key = expense[groupBy] || 'unknown'
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(expense)
    return groups
  }, {})
}
/**
 * Gets expenses statistics by status
 *
 * @param expenses - Array of expense objects
 * @returns Object with counts and amounts by status
 */
export const getExpenseStatsByStatus = (
  expenses: any[],
): Record<string, any> => {
  if (!Array.isArray(expenses)) {
    return {
      pending: { count: 0, amount: 0 },
      approved: { count: 0, amount: 0 },
      rejected: { count: 0, amount: 0 },
      total: { count: 0, amount: 0 },
    }
  }
  const stats = {
    pending: { count: 0, amount: 0 },
    approved: { count: 0, amount: 0 },
    rejected: { count: 0, amount: 0 },
    total: { count: 0, amount: 0 },
  }
  expenses.forEach((expense) => {
    const amount = parseFloat(expense.amount)
    const validAmount = isNaN(amount) ? 0 : amount
    const status = expense.status || 'pending'
    if (stats[status]) {
      stats[status].count++
      stats[status].amount += validAmount
    }
    stats.total.count++
    stats.total.amount += validAmount
  })
  return stats
}
/**
 * Formats expense data for display in charts
 *
 * @param expenses - Array of expense objects
 * @param groupBy - Property to group by (e.g., 'type', 'date')
 * @returns Array formatted for chart display
 */
export const formatExpensesForChart = (
  expenses: any[],
  groupBy: string,
): any[] => {
  if (!Array.isArray(expenses) || expenses.length === 0) return []
  if (groupBy === 'date') {
    // Group by date and sum amounts
    const dateGroups: Record<string, number> = {}
    expenses.forEach((expense) => {
      if (!isValidDate(expense.date)) return
      const dateKey = formatDate(expense.date, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
      const amount = parseFloat(expense.amount)
      if (isNaN(amount)) return
      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = 0
      }
      dateGroups[dateKey] += amount
    })
    // Sort dates chronologically
    return Object.entries(dateGroups)
      .sort(([dateA], [dateB]) => {
        return new Date(dateA).getTime() - new Date(dateB).getTime()
      })
      .map(([date, amount]) => ({
        date,
        amount: Number(amount.toFixed(2)),
      }))
  } else if (groupBy === 'type') {
    // Group by expense type
    const typeGroups: Record<string, number> = {}
    expenses.forEach((expense) => {
      // Make sure we have a valid type and amount
      const type = expense.type || 'other'
      const amount = parseFloat(expense.amount)
      if (isNaN(amount)) return
      // Capitalize first letter for display
      const displayType = type.charAt(0).toUpperCase() + type.slice(1)
      if (!typeGroups[displayType]) {
        typeGroups[displayType] = 0
      }
      typeGroups[displayType] += amount
    })
    // Convert to chart format and sort by value descending
    return Object.entries(typeGroups)
      .map(([name, value]) => ({
        name,
        value: Number(value.toFixed(2)),
      }))
      .sort((a, b) => b.value - a.value)
  } else {
    // For any other grouping (department, employee, etc.)
    const groups = groupExpensesBy(expenses, groupBy || 'type')
    return Object.entries(groups)
      .map(([key, groupExpenses]) => {
        // Use a more descriptive name for unknown values
        const displayName =
          key === 'unknown'
            ? 'Uncategorized'
            : key.charAt(0).toUpperCase() + key.slice(1)
        return {
          name: displayName,
          value: Number(calculateTotalAmount(groupExpenses).toFixed(2)),
        }
      })
      .sort((a, b) => b.value - a.value)
  }
}
// Calculate dashboard statistics from mock data
export const calculateDashboardStats = () => {
  // Get current month data
  const now = new Date()
  const currentMonth = now.getMonth()
  const currentYear = now.getFullYear()
  // Filter expenses for current month
  const currentMonthExpenses = mockExpenses.filter((expense) => {
    const expenseDate = new Date(expense.date)
    return (
      expenseDate.getMonth() === currentMonth &&
      expenseDate.getFullYear() === currentYear
    )
  })
  // Calculate total expenses for current month
  const totalExpenses = currentMonthExpenses.reduce(
    (sum, expense) => sum + parseFloat(expense.amount),
    0,
  )
  // Count expenses by status
  const pendingExpenses = currentMonthExpenses.filter(
    (expense) => expense.status === 'pending',
  )
  const approvedExpenses = currentMonthExpenses.filter(
    (expense) => expense.status === 'approved',
  )
  const rejectedExpenses = currentMonthExpenses.filter(
    (expense) => expense.status === 'rejected',
  )
  const pendingCount = pendingExpenses.length
  const approvedCount = approvedExpenses.length
  const rejectedCount = rejectedExpenses.length
  // Calculate pending amount
  const pendingAmount = pendingExpenses.reduce(
    (sum, expense) => sum + parseFloat(expense.amount),
    0,
  )
  // Calculate approval and rejection rates
  const totalProcessed = approvedCount + rejectedCount
  const approvalRate =
    totalProcessed > 0 ? Math.round((approvedCount / totalProcessed) * 100) : 0
  const rejectionRate =
    totalProcessed > 0 ? Math.round((rejectedCount / totalProcessed) * 100) : 0
  return {
    totalExpenses,
    pendingCount,
    pendingAmount,
    approvedCount,
    rejectedCount,
    approvalRate,
    rejectionRate,
  }
}
// Calculate admin dashboard statistics
export const calculateAdminDashboardStats = () => {
  // Basic employee stats
  const totalEmployees = mockEmployees.length
  const activeEmployees = mockEmployees.filter(
    (emp) => emp.role !== 'inactive',
  ).length
  // Count unique departments
  const departments: Record<string, number> = {}
  mockEmployees.forEach((emp) => {
    if (emp.department) {
      departments[emp.department] = (departments[emp.department] || 0) + 1
    }
  })
  const departmentCount = Object.keys(departments).length
  // Find department with most expenses
  const expensesByDepartment: Record<string, number> = {}
  mockExpenses.forEach((expense) => {
    const employee = mockEmployees.find((emp) => emp.id === expense.employeeId)
    if (employee && employee.department) {
      expensesByDepartment[employee.department] =
        (expensesByDepartment[employee.department] || 0) +
        parseFloat(expense.amount)
    }
  })
  let topDepartment = ''
  let maxExpense = 0
  for (const dept in expensesByDepartment) {
    if (expensesByDepartment[dept] > maxExpense) {
      maxExpense = expensesByDepartment[dept]
      topDepartment = dept
    }
  }
  // Get current month expenses
  const now = new Date()
  const currentMonth = now.getMonth()
  const currentYear = now.getFullYear()
  const currentMonthExpenses = mockExpenses.filter((expense) => {
    const expenseDate = new Date(expense.date)
    return (
      expenseDate.getMonth() === currentMonth &&
      expenseDate.getFullYear() === currentYear
    )
  })
  // Calculate monthly expenses
  const monthlyExpenses = currentMonthExpenses.reduce(
    (sum, expense) => sum + parseFloat(expense.amount),
    0,
  )
  // Get previous month for comparison
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1
  const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear
  const prevMonthExpenses = mockExpenses.filter((expense) => {
    const expenseDate = new Date(expense.date)
    return (
      expenseDate.getMonth() === prevMonth &&
      expenseDate.getFullYear() === prevYear
    )
  })
  const prevMonthTotal = prevMonthExpenses.reduce(
    (sum, expense) => sum + parseFloat(expense.amount),
    0,
  )
  // Calculate growth percentage
  const expenseGrowth =
    prevMonthTotal > 0
      ? Math.round(((monthlyExpenses - prevMonthTotal) / prevMonthTotal) * 100)
      : 100
  // Count pending approvals
  const pendingApprovals = mockExpenses.filter(
    (expense) => expense.status === 'pending',
  ).length
  const pendingAmount = mockExpenses
    .filter((expense) => expense.status === 'pending')
    .reduce((sum, expense) => sum + parseFloat(expense.amount), 0)
  return {
    totalEmployees,
    activeEmployees,
    departmentCount,
    topDepartment,
    monthlyExpenses,
    expenseGrowth,
    pendingApprovals,
    pendingAmount,
  }
}
// Generate expense breakdown by type
export const getExpensesByType = () => {
  const expenseTypes: Record<string, number> = {}
  let total = 0
  // Count expenses by type
  mockExpenses.forEach((expense) => {
    const amount = parseFloat(expense.amount)
    expenseTypes[expense.type] = (expenseTypes[expense.type] || 0) + amount
    total += amount
  })
  // Format for pie chart
  const formattedData = Object.keys(expenseTypes).map((type) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1), // Capitalize first letter
    value: Math.round(expenseTypes[type] * 100) / 100, // Round to 2 decimal places
    percentage: Math.round((expenseTypes[type] / total) * 100),
  }))
  return formattedData
}
// Generate monthly expense data for the past 6 months
export const getMonthlyExpenseData = () => {
  const months = []
  const now = new Date()
  // Get data for the past 6 months
  for (let i = 5; i >= 0; i--) {
    const month = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const monthName = month.toLocaleString('default', { month: 'short' })
    const monthYear = `${monthName} ${month.getFullYear()}`
    const monthExpenses = mockExpenses.filter((expense) => {
      const expenseDate = new Date(expense.date)
      return (
        expenseDate.getMonth() === month.getMonth() &&
        expenseDate.getFullYear() === month.getFullYear()
      )
    })
    const totalAmount = monthExpenses.reduce(
      (sum, expense) => sum + parseFloat(expense.amount),
      0,
    )
    months.push({
      name: monthName,
      value: Math.round(totalAmount * 100) / 100,
    })
  }
  return months
}
// Generate approval trend data for the past 6 months
export const getApprovalTrendData = () => {
  const trends = []
  const now = new Date()
  // Get data for the past 6 months
  for (let i = 5; i >= 0; i--) {
    const month = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const monthName = month.toLocaleString('default', { month: 'short' })
    const monthExpenses = mockExpenses.filter((expense) => {
      const expenseDate = new Date(expense.date)
      return (
        expenseDate.getMonth() === month.getMonth() &&
        expenseDate.getFullYear() === month.getFullYear()
      )
    })
    const approved = monthExpenses.filter(
      (expense) => expense.status === 'approved',
    ).length
    const rejected = monthExpenses.filter(
      (expense) => expense.status === 'rejected',
    ).length
    const pending = monthExpenses.filter(
      (expense) => expense.status === 'pending',
    ).length
    const total = monthExpenses.length || 1 // Avoid division by zero
    trends.push({
      name: monthName,
      approved: Math.round((approved / total) * 100),
      rejected: Math.round((rejected / total) * 100),
      pending: Math.round((pending / total) * 100),
    })
  }
  return trends
}
